/**
 * Where a friend goes on the map, and how the arrow and the arrival rule treat them, from whatever the engine
 * has for them right now: UWB (distance, sometimes direction), GPS (from the server, or carried over Bluetooth
 * when there is no service), or Bluetooth presence alone. One pure function, so the rule can be read, unit-tested
 * and replayed from logs (Sam, 2026-10-04: "that'll help us QA what's going on").
 *
 * The rule, in order:
 *   1. UWB with direction, confirmed for UWB_CONFIRM_MS: you + the UWB distance along the UWB bearing. Never a UWB
 *      distance along a GPS bearing - that mix is what made a friend swing across the map.
 *   2. UWB has just dropped (less than UWB_HOLD_MS ago): stay where UWB last put them, so a blink in the session
 *      does not snap them back to a 10 m-old GPS fix and then forward again.
 *   3. UWB distance without direction: the GPS spot, but the distance is UWB's, and a ring at that distance
 *      around you says how far without claiming where. No bearing and no arrow: GPS's direction is not UWB's.
 *   4. Bluetooth hears them now and their GPS is stale (or absent): nowhere to draw them, no Go target; "nearby"
 *      at the radar's RSSI band. The radar's rule, so the two pages agree (2026-10-16 audit: the map drew a friend
 *      who went offline beside you at their 20-minute-old fix, and Go walked you away from them).
 *   5. A GPS fix (the server's or Bluetooth-carried, treated alike - Bluetooth's is just fresher).
 *
 * The source never shows in the interface (Sam, 2026-10-04): what changes is where the puck is, how fast it
 * moves and when you have arrived. The result carries a `glideMs`: the renderer eases the token to its target over
 * that long (fast for UWB, slow for GPS, none for a jump across the field), so a change of source never teleports
 * it. Arrival and the arrow scale with the source: UWB is good to centimetres, GPS to a dozen metres.
 */
import { bearingDegrees, destinationPoint, haversineMeters } from '../proximity/engine/geodesy';
import { BLE_NEARBY_HOLD_MS, GPS_STALE_THRESHOLD_MS } from '../proximity/engine/peerDisplay';

export const PLACEMENT = {
  /** UWB with direction has to be present this long before the puck follows it (one-reading blips are ignored). */
  UWB_CONFIRM_MS: 600,
  /** After UWB drops, the UWB-placed spot is kept this long before GPS takes over again. */
  UWB_HOLD_MS: 4000,
  /** Glide time constants for the renderer: how long until the token has covered ~63% of the way to a new target. */
  UWB_GLIDE_MS: 250,
  GPS_GLIDE_MS: 1500,
  /** A GPS target this far from where the puck is drawn is a different place, not a drift: jump. */
  GPS_JUMP_M: 60,
  /** Arrival radius and the distance under which the arrow retires, by source. */
  ARRIVE_UWB_M: 2,
  ARRIVE_GPS_M: 12,
  /** Tokens are 2.3 m across: inside this the friend's token touches yours and an arrow says nothing. */
  ARROW_MIN_UWB_M: 3,
  /** A fix older than this is "where they were", not "where they are" (shared with the map's expiry). */
  GPS_EXPIRE_MS: 6 * 60 * 60 * 1000,
};

/** The tracker keeps per-peer glide state; one per map. */
export function createPlacementTracker() {
  const state = new Map();
  return {
    place(peerId, input, now = Date.now()) {
      const prev = state.get(peerId) ?? null;
      const out = placePeer({ ...input, prev, now });
      if (out.state) state.set(peerId, out.state);
      else state.delete(peerId);
      return out;
    },
    get(peerId) {
      return state.get(peerId) ?? null;
    },
    forget(peerId) {
      state.delete(peerId);
    },
    reset() {
      state.clear();
    },
  };
}

/**
 * @param {object} a
 * @param {{lat:number,lng:number,accuracyMeters?:number|null}|null} a.self   your fix
 * @param {{lat:number,lng:number,accuracyMeters?:number|null,lastKnownAt?:number|null,source?:string}|null} a.gps  their merged fix
 * @param {{distanceM:number,bearingDeg?:number|null,directionAvailable?:boolean,active?:boolean}|null} a.uwb
 * @param {{lastSeenAt?:number|null,estimatedDistanceM?:number|null}|null} a.ble
 * @param {{kind?:string|null,distanceMeters?:number|null}|null} [a.display]  the radar's resolved display for this
 *   peer (resolvePeerDisplay); when given, its kind decides Bluetooth-nearby so the map and the radar cannot disagree
 * @param {object|null} a.prev   the tracker's state for this peer
 * @param {number} a.now
 */
export function placePeer({ self, gps, uwb, ble, display = null, prev, now }) {
  const P = PLACEMENT;
  const uwbOn = Boolean(uwb && uwb.active !== false && Number.isFinite(uwb.distanceM));
  const uwbDir =
    uwbOn && uwb.directionAvailable === true && Number.isFinite(uwb.bearingDeg) && self != null;
  const gpsFresh = Boolean(
    gps &&
    Number.isFinite(gps.lat) &&
    Number.isFinite(gps.lng) &&
    (!Number.isFinite(gps.lastKnownAt) || now - gps.lastKnownAt <= P.GPS_EXPIRE_MS),
  );
  // Bluetooth-nearby beats a fix the radar calls stale (GPS_STALE_THRESHOLD_MS): the radar's own verdict when we have
  // it, else the same rule from the same constants - heard within the nearby hold, and no fix younger than the bar.
  const bleHeard = Boolean(
    ble && (ble.lastSeenAt == null || now - ble.lastSeenAt < BLE_NEARBY_HOLD_MS),
  );
  const gpsStale =
    !gpsFresh ||
    !Number.isFinite(gps.lastKnownAt) ||
    now - gps.lastKnownAt > GPS_STALE_THRESHOLD_MS;
  const bleNearby = display?.kind ? display.kind === 'ble-in-range' : bleHeard && gpsStale;

  // 1 + 2: when did UWB-with-direction start, and when was it last seen
  const uwbSince = uwbDir ? (prev?.uwbSince ?? now) : null;
  const uwbConfirmed = uwbDir && now - uwbSince >= P.UWB_CONFIRM_MS;
  const uwbLastAt = uwbConfirmed ? now : (prev?.uwbLastAt ?? null);
  const holding =
    !uwbConfirmed && uwbLastAt != null && now - uwbLastAt < P.UWB_HOLD_MS && prev?.lat != null;

  let source = 'none';
  let target = null;
  if (uwbConfirmed) {
    source = 'uwb';
    target = destinationPoint(self.lat, self.lng, uwb.distanceM, uwb.bearingDeg);
  } else if (holding) {
    source = 'uwb';
    target = { lat: prev.lat, lng: prev.lng };
  } else if (bleNearby) {
    source = 'ble';
  } else if (gpsFresh) {
    source = 'gps';
    target = { lat: gps.lat, lng: gps.lng };
  }

  // The target, and how fast the renderer should take the token there
  const lat = target?.lat ?? null,
    lng = target?.lng ?? null;
  const far =
    target &&
    prev?.lat != null &&
    haversineMeters(prev.lat, prev.lng, target.lat, target.lng) > P.GPS_JUMP_M;
  const glideMs =
    !target || prev?.lat == null || (far && source === 'gps')
      ? 0
      : source === 'uwb'
        ? P.UWB_GLIDE_MS
        : P.GPS_GLIDE_MS;

  // Distance and bearing: UWB's whenever UWB has a range (even without direction), else from the drawn spot
  let distanceM = null;
  if (uwbOn) distanceM = uwb.distanceM;
  else if (lat != null && self) distanceM = haversineMeters(self.lat, self.lng, lat, lng);
  // Bluetooth: the radar's band distance when we have it (the number the radar and the row show), else the estimate.
  else if (
    source === 'ble' &&
    display?.kind === 'ble-in-range' &&
    Number.isFinite(display.distanceMeters)
  )
    distanceM = display.distanceMeters;
  else if (source === 'ble' && Number.isFinite(ble?.estimatedDistanceM))
    distanceM = ble.estimatedDistanceM;
  // A UWB range drawn at a GPS spot (rule 3) gets no bearing: the arrow would point a UWB distance along GPS's
  // direction. The ring says how far; the arrow comes back with UWB's own direction.
  let bearing = null;
  if (uwbDir) bearing = uwb.bearingDeg;
  else if (lat != null && self && !(uwbOn && source === 'gps'))
    bearing = bearingDegrees(self.lat, self.lng, lat, lng);

  const precise = uwbOn;
  const arriveM = precise ? P.ARRIVE_UWB_M : P.ARRIVE_GPS_M;
  const arrowMinM = precise ? P.ARROW_MIN_UWB_M : P.ARRIVE_GPS_M;
  const arrow = bearing != null && distanceM != null && distanceM > arrowMinM;
  const ring = uwbOn && !uwbDir && lat != null ? uwb.distanceM : null;

  return {
    source,
    lat,
    lng,
    distanceM,
    bearingDeg: bearing,
    arrow,
    arrowMinM,
    arriveM,
    ring,
    glideMs,
    // UWB range error is centimetres, so a UWB-placed friend inherits only OUR fix error.
    accuracyMeters:
      source === 'uwb' ? (self?.accuracyMeters ?? null) : (gps?.accuracyMeters ?? null),
    lastKnownAt: source === 'uwb' ? now : (gps?.lastKnownAt ?? null),
    fused: source === 'uwb',
    state: target
      ? { lat, lng, at: now, source, uwbSince, uwbLastAt }
      : uwbLastAt != null
        ? { ...(prev ?? {}), uwbLastAt }
        : null,
  };
}
